import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { SourceMarketFields } from '@aviation/catering-masterdata-sdk';
import { DynamoDbObject } from '@aviation/catering-common';
import { Status, useFetch } from './useFetch';

export interface ISourceMarketContext {
	status: Status;
	sourceMarkets: Array<SourceMarketFields & DynamoDbObject>;
	// Forces every consumer to refetch, e.g. after a source market was created/updated/deleted.
	refresh: () => void;
}

const SourceMarketContext = createContext<ISourceMarketContext>({
	status: Status.Idle,
	sourceMarkets: [],
	refresh: () => {},
});

type Props = {
	children?: ReactNode;
};

// Fetches the SourceMarket list once and shares it across the app, so an update made on one
// page (e.g. the Source Markets admin grid) is immediately visible everywhere else (nav,
// route guards, the client switcher) without a page reload.
export const SourceMarketProvider: React.FC<Props> = ({ children }) => {
	const [refreshCounter, setRefreshCounter] = useState(0);
	const { status, data: sourceMarkets = [] } = useFetch<Array<SourceMarketFields & DynamoDbObject>>(`/api/masterdata/sourcemarkets`, undefined, refreshCounter);
	const refresh = useCallback(() => setRefreshCounter(c => c + 1), []);

	return <SourceMarketContext.Provider value={{ status, sourceMarkets, refresh }}>{children}</SourceMarketContext.Provider>;
};

// Raw list + status + refresh trigger.
export const useSourceMarkets = (): ISourceMarketContext => {
	return useContext(SourceMarketContext);
};

// Resolves the SourceMarket record (and its feature flags) for a given client code.
export const useSourceMarket = (clientCode?: string): { status: Status, sourceMarket?: SourceMarketFields & DynamoDbObject, sourceMarkets: Array<SourceMarketFields & DynamoDbObject>, refresh: () => void } => {
	const { status, sourceMarkets, refresh } = useContext(SourceMarketContext);
	const sourceMarket = sourceMarkets.find(o => o.ClientCode === clientCode);

	return { status, sourceMarket, sourceMarkets, refresh };
};
